import { showToast } from '../../../graphql/cache/modal';
import { sleep } from '../../../utilities/time';
import { getRoute, paths } from '../../../constants/strings';
import { keys } from '../../../utilities/translator/translation_keys';

const REQUEST_THROTTLE = 100;

// Maps an exported action's `actionable` reference onto the actionableType/actionableId
// pair the addConditionalAction mutation expects. Category/question IDs are remapped
// through categoryIdMap/questionIdMap since those entities get new IDs on import;
// template and user IDs are external references and are passed through as-is.
const resolveActionable = (action, categoryIdMap, questionIdMap) => {
  const actionable = action.actionable;
  if (!actionable) return { actionableType: null, actionableId: null };

  switch (action.action) {
    case 'FOLLOW_UP_CATEGORY':
      return {
        actionableType: 'inspectionTemplateCategory',
        actionableId: categoryIdMap[actionable.id] ?? null,
      };
    case 'PREFILL_ANSWER':
      return {
        actionableType: 'inspectionTemplateItem',
        actionableId: questionIdMap[actionable.id] ?? null,
      };
    case 'FOLLOW_UP_TEMPLATE':
      return {
        actionableType: 'inspectionTemplate',
        actionableId: actionable.id,
      };
    case 'SEND_NOTIFICATION':
      return { actionableType: 'user', actionableId: actionable.id };
    default:
      return { actionableType: null, actionableId: null };
  }
};

export const handleJsonImport = async (
  e,
  {
    createTemplate,
    addCategory,
    addQuestion,
    addConditionalLogic,
    addConditionalAction,
    setImporting,
    setImportProgress,
    workspaceId,
    onHide,
    navigate,
    t,
  },
) => {
  const file = e.target.files[0];
  if (!file) return;
  e.target.value = '';

  if (!file.name.endsWith('.opas.json')) {
    showToast({
      title: t(keys.templates.JSON_INVALID_FILE_TITLE),
      message: t(keys.templates.JSON_INVALID_FILE_MESSAGE),
      variant: 'danger',
    });
    return;
  }

  let parsed;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    showToast({
      title: t(keys.templates.JSON_INVALID_PARSE_TITLE),
      message: t(keys.templates.JSON_INVALID_PARSE_MESSAGE),
      variant: 'danger',
    });
    return;
  }

  const { version, template: tmpl } = parsed;
  if (
    version !== 1 ||
    !tmpl?.title ||
    !tmpl?.templateType ||
    !Array.isArray(tmpl?.categories)
  ) {
    showToast({
      title: t(keys.templates.JSON_INVALID_FORMAT_TITLE),
      message: t(keys.templates.JSON_INVALID_FORMAT_MESSAGE),
      variant: 'danger',
    });
    return;
  }

  const allQuestions = tmpl.categories.flatMap((c) => c.questions);
  const allQuestionLogic = allQuestions.flatMap(
    (q) => q.conditionalAction || [],
  );
  const allQuestionActions = allQuestionLogic.flatMap((l) => l.actions || []);
  const followUpCount = allQuestionActions.filter(
    (a) => a.action === 'FOLLOW_UP_QUESTION',
  ).length;
  const templateLogic = tmpl.conditionalAction || [];
  const templateActions = templateLogic.flatMap((l) => l.actions || []);

  const totalSteps =
    1 +
    tmpl.categories.length +
    allQuestions.length +
    allQuestionLogic.length +
    allQuestionActions.length +
    followUpCount +
    templateLogic.length +
    templateActions.length;

  let completed = 0;
  const tick = () => {
    completed++;
    setImportProgress(Math.round((completed / totalSteps) * 100));
  };

  setImporting(true);
  setImportProgress(0);

  try {
    // 1. Create the template shell
    const {
      data: {
        addInspectionTemplate: { id: newTemplateId },
      },
    } = await createTemplate({
      variables: {
        title: tmpl.title,
        description: tmpl.description || '',
        templateType: tmpl.templateType,
        reviewRequired: tmpl.reviewRequired || false,
        analysisRequested: tmpl.analysisRequested || false,
        workspaceId: workspaceId || null,
      },
    });
    tick();

    // Map old question IDs → new question IDs (needed for FOLLOW_UP_QUESTION)
    const questionIdMap = {};
    // Map old category IDs → new category IDs (needed for FOLLOW_UP_CATEGORY)
    const categoryIdMap = {};

    // 2. Categories then questions
    for (const category of tmpl.categories) {
      await sleep(REQUEST_THROTTLE);
      const {
        data: {
          addInspectionTemplateCategory: { id: newCategoryId },
        },
      } = await addCategory({
        variables: {
          templateId: newTemplateId,
          title: category.title,
          isBundle: category.isBundle || false,
        },
      });
      categoryIdMap[category.id] = newCategoryId;
      tick();

      for (const question of category.questions) {
        await sleep(REQUEST_THROTTLE);
        const {
          data: {
            addInspectionTemplateItem: { id: newQuestionId },
          },
        } = await addQuestion({
          variables: {
            categoryId: newCategoryId,
            templateId: newTemplateId,
            title: question.title,
            description: question.description || '',
            inputType: question.inputType,
            required: question.required || false,
            additionalData: question.additionalData || null,
            questionOptions: question.questionOptions || null,
          },
        });
        questionIdMap[question.id] = newQuestionId;
        tick();
      }
    }

    // 3. Question-level conditional logic and their actions
    for (const category of tmpl.categories) {
      for (const question of category.questions) {
        for (const logic of question.conditionalAction || []) {
          await sleep(REQUEST_THROTTLE);
          const {
            data: {
              addConditionalLogic: { id: newLogicId },
            },
          } = await addConditionalLogic({
            variables: {
              workableId: questionIdMap[question.id],
              workableType: 'inspectionTemplateItem',
              description: logic.description || '',
              operator: logic.operator,
              value: logic.value || '',
            },
          });
          tick();

          for (const action of logic.actions || []) {
            await sleep(REQUEST_THROTTLE);
            if (action.action === 'FOLLOW_UP_QUESTION' && action.actionable) {
              const fu = action.actionable;
              const {
                data: {
                  addInspectionTemplateItem: { id: newFuId },
                },
              } = await addQuestion({
                variables: {
                  templateId: newTemplateId,
                  title: fu.title,
                  description: fu.description || '',
                  inputType: fu.inputType,
                  required: fu.required || false,
                  additionalData: fu.additionalData || null,
                  questionOptions: fu.questionOptions || null,
                  conditionalActionId: newLogicId,
                },
              });
              questionIdMap[fu.id] = newFuId;
              tick(); // extra tick for the follow-up question creation
            } else {
              const { actionableType, actionableId } = resolveActionable(
                action,
                categoryIdMap,
                questionIdMap,
              );
              await addConditionalAction({
                variables: {
                  conditionalActionId: newLogicId,
                  action: action.action,
                  additionalData: action.additionalData || null,
                  actionableType,
                  actionableId,
                },
              });
            }
            tick();
          }
        }
      }
    }

    // 4. Template-level conditional logic and their actions
    for (const logic of tmpl.conditionalAction || []) {
      await sleep(REQUEST_THROTTLE);
      const {
        data: {
          addConditionalLogic: { id: newLogicId },
        },
      } = await addConditionalLogic({
        variables: {
          workableId: newTemplateId,
          workableType: 'inspectionTemplate',
          description: logic.description || '',
          operator: logic.operator,
          value: logic.value || '',
        },
      });
      tick();

      for (const action of logic.actions || []) {
        await sleep(REQUEST_THROTTLE);
        const { actionableType, actionableId } = resolveActionable(
          action,
          categoryIdMap,
          questionIdMap,
        );
        await addConditionalAction({
          variables: {
            conditionalActionId: newLogicId,
            action: action.action,
            additionalData: action.additionalData || null,
            actionableType,
            actionableId,
          },
        });
        tick();
      }
    }

    onHide();
    navigate(getRoute(workspaceId, paths.templates, newTemplateId));
  } catch (err) {
    console.error('JSON template import failed:', err);
    showToast({
      title: t(keys.templates.JSON_IMPORT_FAILED_TITLE),
      message: t(keys.templates.JSON_IMPORT_FAILED_MESSAGE),
      variant: 'danger',
    });
    setImporting(false);
    setImportProgress(0);
  }
};
